// apps/web/src/features/admin/form-config/DraftBanner.tsx
import { useEffect, useRef, useState } from 'react';
import type { ValidationError } from '@capture/shared';

interface DraftBannerProps {
  state: 'draft' | 'published' | 'archived';
  saving: boolean;
  errorCount: number;
  errors?: Map<string, ValidationError[]>;
  lastSavedAt?: Date | null;
  onPublish: () => void;
  onDiscard: () => void;
  /**
   * F-013 — when set, an error carrying a `linkTarget` renders as a button that
   * jumps to the offending field's tab/field (same handler PublishModal uses).
   */
  onErrorClick?: (target: NonNullable<ValidationError['linkTarget']>) => void;
  /**
   * F-032 — when true, another surface wrote the draft since this one loaded
   * it. The banner becomes a blocking reload prompt: Publish/Discard are hidden
   * (both act on state this screen can no longer reason about) and `onReload`
   * refetches the draft.
   */
  conflicted?: boolean;
  onReload?: () => void;
  'data-testid'?: string;
}

export function DraftBanner({
  state,
  saving,
  errorCount,
  errors,
  lastSavedAt,
  onPublish,
  onDiscard,
  onErrorClick,
  conflicted,
  onReload,
  'data-testid': dataTestId,
}: DraftBannerProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Click outside closes popover.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!popoverRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (state !== 'draft') return null;

  // F-032 — conflict takes over the whole banner: autosave is suspended and
  // the only safe action is reloading the newer server draft.
  if (conflicted) {
    return (
      <div className="draft-banner draft-banner--conflict" role="alert" data-testid={dataTestId}>
        <span className="draft-banner__label">
          This draft changed in another tab or screen. Reload to continue — unsaved edits on this
          screen will be lost.
        </span>
        <button type="button" onClick={onReload}>Reload</button>
      </div>
    );
  }

  const flatErrors = errors ? collectErrors(errors) : [];

  return (
    <div className="draft-banner" role="status" data-testid={dataTestId}>
      <span className="draft-banner__label">Draft — unpublished changes</span>
      {saving && <span className="draft-banner__saving">Saving…</span>}
      {!saving && lastSavedAt && (
        <span className="draft-banner__saved">Saved at {lastSavedAt.toLocaleTimeString()}</span>
      )}
      {errorCount > 0 && (
        <div className="draft-banner__errors-wrap" ref={popoverRef}>
          <button
            type="button"
            className="draft-banner__errors"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-haspopup="dialog"
          >
            {errorCount} error{errorCount === 1 ? '' : 's'}
            <span className="draft-banner__errors-caret" aria-hidden="true">▾</span>
          </button>
          {open && flatErrors.length > 0 && (
            <div className="draft-banner__error-popover" role="dialog" aria-label="Validation errors">
              <ul className="draft-banner__error-list">
                {flatErrors.map((e, i) => {
                  // F-013 — clickable when the error knows its UX home and a
                  // navigation handler is provided; otherwise plain text.
                  const canLink = !!(e.linkTarget?.tab && onErrorClick);
                  return (
                    <li key={i} className="draft-banner__error-item">
                      {canLink ? (
                        <button
                          type="button"
                          className="draft-banner__error-message draft-banner__error-link"
                          onClick={() => {
                            onErrorClick!(e.linkTarget!);
                            setOpen(false);
                          }}
                        >
                          {e.message}
                        </button>
                      ) : (
                        <div className="draft-banner__error-message">{e.message}</div>
                      )}
                      <div className="draft-banner__error-meta">
                        <code className="draft-banner__error-code">{e.code}</code>
                        {e.fieldName && <span className="draft-banner__error-field">field: {e.fieldName}</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
      <button type="button" onClick={onDiscard}>Discard</button>
      <button type="button" onClick={onPublish} disabled={errorCount > 0}>Publish</button>
    </div>
  );
}

function collectErrors(map: Map<string, ValidationError[]>): ValidationError[] {
  const out: ValidationError[] = [];
  for (const arr of map.values()) {
    for (const e of arr) if (e.severity === 'error') out.push(e);
  }
  return out;
}
